/**
 * signalNotificationStore
 *
 * Unified store for the signal notification domain. Merges what was previously
 * two separate stores:
 *
 *   - signalNotificationStore  (API polling — unread count badge, notification items)
 *   - signalNotificationsStore (WS real-time — Signal carousel, unreadIds Set)
 *
 * NotificationBell uses the API-polling slice (items, unreadCount, loadRecent, …).
 * SignalPopup uses the WS real-time slice (signals, addSignal, loadInitial, …).
 * Both slices share a single Zustand atom so cross-slice reads are free.
 */
import { create } from 'zustand';
import type { Signal } from '@trading/shared';
import {
  signalNotificationsService,
  type SignalNotificationItem,
} from '../services/signalNotifications.service';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_RECENT = 20; // max signals kept in the WS carousel

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

interface SignalNotificationState {
  // --- API-polling slice (NotificationBell) ---

  /** Unread count badge value from the API. */
  unreadCount: number;
  /** Recent notification items shown in the dropdown. */
  items: SignalNotificationItem[];
  /** True while the initial count/recent fetch is in flight. */
  isLoading: boolean;

  // --- WS real-time slice (SignalPopup) ---

  /** Ordered list of recent signals, newest first. Capped at MAX_RECENT. */
  signals: Signal[];
  /** IDs of signals the user has not yet dismissed/viewed (WS carousel). */
  unreadIds: Set<string>;

  // ---------------------------------------------------------------------------
  // Actions — stable references, safe in React dependency arrays
  // ---------------------------------------------------------------------------

  // API-polling actions (NotificationBell)
  loadUnreadCount: () => Promise<void>;
  loadRecent: (limit?: number) => Promise<void>;
  /** Mark a SignalNotificationItem as read by its notification id. */
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;

  // WS real-time actions (SignalPopup)
  /** Add a new signal (from WS or initial fetch). De-duplicates by id. */
  addSignal: (signal: Signal) => void;
  /** Bulk-load signals from the initial REST fetch (called on mount). */
  loadInitial: (signals: Signal[]) => void;
  /** Mark a signal in the WS carousel as read by its signal id. */
  markSignalRead: (signalId: string) => void;
  /** Mark all WS carousel signals as read. */
  markAllSignalsRead: () => void;
  /** Remove a signal from the WS carousel (after dismiss API call). */
  removeSignal: (signalId: string) => void;
  /** Unread count for the WS carousel — convenience selector (returns a number). */
  wsUnreadCount: () => number;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useSignalNotificationStore = create<SignalNotificationState>()(
  (set, get) => ({
    // Initial state — API slice
    unreadCount: 0,
    items: [],
    isLoading: false,

    // Initial state — WS slice
    signals: [],
    unreadIds: new Set<string>(),

    // -------------------------------------------------------------------------
    // API-polling actions
    // -------------------------------------------------------------------------

    loadUnreadCount: async () => {
      try {
        const count = await signalNotificationsService.getUnreadCount();
        set({ unreadCount: count });
      } catch {
        // Silently ignore — badge will just stay at its last value
      }
    },

    loadRecent: async (limit = 10) => {
      set({ isLoading: true });
      try {
        const items = await signalNotificationsService.getRecent(limit);
        const unreadCount = items.filter((n) => n.viewedAt === null).length;
        set({ items, unreadCount, isLoading: false });
      } catch {
        set({ isLoading: false });
      }
    },

    markRead: async (id: string) => {
      try {
        await signalNotificationsService.markViewed(id);
        // Optimistically update local state
        set((state) => ({
          items: state.items.map((n) =>
            n.id === id && n.viewedAt === null
              ? { ...n, viewedAt: new Date().toISOString() }
              : n
          ),
          unreadCount: Math.max(
            0,
            state.unreadCount -
              (state.items.find((n) => n.id === id && n.viewedAt === null) ? 1 : 0)
          ),
        }));
      } catch {
        // Silently ignore — the notification remains marked unread
      }
    },

    markAllRead: async () => {
      try {
        await signalNotificationsService.markAllRead();
        const now = new Date().toISOString();
        set((state) => ({
          items: state.items.map((n) =>
            n.viewedAt === null ? { ...n, viewedAt: now } : n
          ),
          unreadCount: 0,
        }));
      } catch {
        // Silently ignore; next loadUnreadCount will reconcile
        void get().loadUnreadCount();
      }
    },

    // -------------------------------------------------------------------------
    // WS real-time actions
    // -------------------------------------------------------------------------

    addSignal: (signal) => {
      set((state) => {
        // De-duplicate: if already present, move to front
        const filtered = state.signals.filter((s) => s.id !== signal.id);
        const next = [signal, ...filtered].slice(0, MAX_RECENT);
        const newUnread = new Set(state.unreadIds);
        newUnread.add(signal.id);
        return { signals: next, unreadIds: newUnread };
      });
    },

    loadInitial: (signals) => {
      set((state) => {
        // Merge with any signals already added via WS; WS signals take precedence
        // (they're already in state.signals). Don't overwrite with REST data.
        const existingIds = new Set(state.signals.map((s) => s.id));
        const newOnes = signals.filter((s) => !existingIds.has(s.id));
        const merged = [...state.signals, ...newOnes].slice(0, MAX_RECENT);

        const newUnread = new Set(state.unreadIds);
        for (const s of newOnes) newUnread.add(s.id);

        return { signals: merged, unreadIds: newUnread };
      });
    },

    markSignalRead: (signalId) => {
      set((state) => {
        const newUnread = new Set(state.unreadIds);
        newUnread.delete(signalId);
        return { unreadIds: newUnread };
      });
    },

    markAllSignalsRead: () => {
      set({ unreadIds: new Set<string>() });
    },

    removeSignal: (signalId) => {
      set((state) => {
        const newUnread = new Set(state.unreadIds);
        newUnread.delete(signalId);
        return {
          signals: state.signals.filter((s) => s.id !== signalId),
          unreadIds: newUnread,
        };
      });
    },

    wsUnreadCount: () => get().unreadIds.size,
  })
);

